import { useState } from "react";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { Switch } from "@/components/ui/library/switch";
import type { ToolActionView } from "../../types";

interface Props {
  action: ToolActionView;
  isLoading: boolean;
  onClose: () => void;
  onRun: (input: Record<string, unknown>) => void;
}

/** Asks what an action needs before it runs: its own fields and any confirmation. */
export default function RunActionDialog({ action, isLoading, onClose, onRun }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const set = (key: string, value: unknown) => setValues((v) => ({ ...v, [key]: value }));
  const missing = action.inputs.some(
    (i) => i.required && (values[i.key] === undefined || values[i.key] === ""),
  );

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => onRun(values)}
      title={action.label}
      tone="info"
      hideCallout={!action.confirm}
      warningTitle={action.confirm ?? undefined}
      confirmLabel={action.label}
      loadingLabel="Working…"
      isLoading={isLoading}
      disabled={missing}
      size="md"
    >
      {action.inputs.length ? (
        <div className="flex flex-col gap-3 px-6 pb-2">
          {action.inputs.map((input) => (
            <label key={input.key} className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-[#051912]">
                {input.label}
                {input.required ? <span className="text-[#da2a46]"> *</span> : null}
              </span>
              {input.type === "boolean" ? (
                <Switch checked={!!values[input.key]} onCheckedChange={(on) => set(input.key, on)} />
              ) : input.type === "select" ? (
                <select
                  value={String(values[input.key] ?? "")}
                  onChange={(e) => set(input.key, e.target.value)}
                  className="h-10 rounded-md border border-[#e6e8e7] bg-white px-3 text-[13px] focus:border-[#156548] focus:outline-none"
                >
                  <option value="">Choose…</option>
                  {input.options?.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={input.type === "number" ? "number" : "text"}
                  value={String(values[input.key] ?? "")}
                  onChange={(e) => set(input.key, e.target.value)}
                  className="h-10 rounded-md border border-[#e6e8e7] bg-white px-3 text-[13px] focus:border-[#156548] focus:outline-none"
                />
              )}
              {input.help ? <span className="text-[12px] text-[#5b6b64]">{input.help}</span> : null}
            </label>
          ))}
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
